import type {Icon} from '@primer/octicons-react'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {ProgressBar, Tooltip, VisuallyHidden} from '@primer/react'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import {clsx} from 'clsx'
import {
  createContext,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import styles from './MiniMergeSections.module.css'
import {MiniStatusIcon, type MiniStatusIconProps} from './MiniStatusIcon'
import {Link, type To} from '@github-ui/react-router'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'

type MiniMergeStatusItemProps = PropsWithChildren<{
  'aria-label': string
  'aria-expanded'?: boolean
  isFirst?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  to?: To
}>

const MiniStatusLabelIdContext = createContext<string | undefined>(undefined)
type MiniStatusActionContextValue = {
  actionRef: RefObject<HTMLAnchorElement | HTMLButtonElement | null>
  focusFallback: () => void
  label?: string
  expanded?: boolean
  to?: To
}
const MiniStatusActionContext = createContext<MiniStatusActionContextValue | undefined>(undefined)

export function useMiniStatusAction() {
  return use(MiniStatusActionContext)
}

function MiniMergeStatusItem({
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  children,
  isFirst = false,
  onClick,
  to,
}: MiniMergeStatusItemProps) {
  const labelId = useId()
  const actionId = useId()
  const isRefreshEnabled = isForYouRefreshEnabled()
  const actionRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
  const fallbackRef = useRef<HTMLElement>(null)
  const focusFallback = useCallback(() => fallbackRef.current?.focus(), [])
  const interactive = Boolean(onClick || to)
  const action = useMemo(
    () => ({actionRef, focusFallback, label: interactive ? ariaLabel : undefined, expanded: ariaExpanded, to}),
    [focusFallback, interactive, ariaLabel, ariaExpanded, to],
  )
  const Container = isRefreshEnabled || !interactive ? 'section' : 'div'

  return (
    <MiniStatusActionContext value={action}>
      <MiniStatusLabelIdContext value={labelId}>
        <Container
          aria-label={isRefreshEnabled || !interactive ? ariaLabel : undefined}
          ref={element => {
            fallbackRef.current = element
          }}
          tabIndex={isRefreshEnabled ? -1 : undefined}
          className={clsx(
            styles.MiniStatus,
            interactive && styles.MiniStatusInteractive,
            isRefreshEnabled && styles.MiniStatusRefresh,
            isRefreshEnabled && isFirst && styles.MiniStatusFirst,
            ariaExpanded && styles.MiniStatusExpanded,
          )}
          data-testid="mini-status"
        >
          {to ? (
            <Link
              ref={element => {
                actionRef.current = element
              }}
              to={to}
              aria-labelledby={`${labelId} ${actionId}`}
              aria-expanded={ariaExpanded}
              className={styles.MiniStatusButton}
            >
              <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
            </Link>
          ) : onClick ? (
            <button
              ref={element => {
                actionRef.current = element
              }}
              aria-labelledby={`${labelId} ${actionId}`}
              aria-expanded={ariaExpanded}
              className={styles.MiniStatusButton}
              onClick={onClick}
              type="button"
            >
              <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
            </button>
          ) : null}
          {children}
        </Container>
      </MiniStatusLabelIdContext>
    </MiniStatusActionContext>
  )
}

function LabelRow({
  children,
  icon,
  summary,
  showNavigationIcon = false,
}: {
  children: ReactNode
  icon?: Icon
  summary?: string
  showNavigationIcon?: boolean
}) {
  const IconComponent = icon ?? (showNavigationIcon ? ArrowUpRightIcon : undefined)
  const summaryRef = useRef<HTMLElement>(null)
  const action = useMiniStatusAction()
  const [isTruncated, setIsTruncated] = useState(false)
  const observeSummary = useResizeObserver(({target}) => {
    setIsTruncated(target.scrollWidth > target.clientWidth)
  })

  useEffect(() => {
    // Reobserve when the copy changes, even if the ellipsized element keeps the same width.
    return observeSummary(summaryRef.current)
  }, [observeSummary, summary])

  let summaryContent: ReactNode = null
  if (summary && action?.label) {
    summaryContent = (
      <Tooltip
        ref={summaryRef}
        direction="n"
        text={isTruncated ? summary : ''}
        type="description"
        _privateDisableTooltip={!isTruncated}
        hidden={!isTruncated}
        data-testid="mini-status-summary-tooltip"
      >
        {action.to ? (
          <Link
            to={action.to}
            aria-label={`${summary}. ${action.label}`}
            className={clsx(styles.MiniStatusSummary, styles.MiniStatusSummaryAction)}
            tabIndex={isTruncated ? 0 : -1}
          >
            {summary}
          </Link>
        ) : (
          <button
            type="button"
            aria-label={`${summary}. ${action.label}`}
            aria-expanded={action.expanded}
            className={clsx(styles.MiniStatusSummary, styles.MiniStatusSummaryAction)}
            tabIndex={isTruncated ? 0 : -1}
            onClick={() => action.actionRef.current?.click()}
          >
            {summary}
          </button>
        )}
      </Tooltip>
    )
  } else if (summary) {
    summaryContent = <span className={styles.MiniStatusSummary}>{summary}</span>
  }

  return (
    <div className={styles.MiniStatusLabelRow}>
      {children}
      {summaryContent}
      {IconComponent ? <IconComponent aria-hidden="true" className={styles.NavigationIcon} /> : null}
    </div>
  )
}
type LabelProps = PropsWithChildren<{
  icon?: MiniStatusIconProps['type']
  isUnavailable?: boolean
  successful?: number
  status?: string
  total?: number
}>

function Label({icon, isUnavailable = false, successful, status, total, children}: LabelProps) {
  const labelId = use(MiniStatusLabelIdContext)
  const hasRatio = successful !== undefined && total !== undefined
  const iconType = isUnavailable ? 'empty' : icon
  const count = hasRatio ? (isUnavailable || total === 0 ? 0 : successful) : undefined
  const isRefreshEnabled = isForYouRefreshEnabled()

  return (
    <span id={labelId} className={clsx('d-flex flex-items-center gap-1', styles.MiniStatusLabel)}>
      {iconType && !isRefreshEnabled ? <MiniStatusIcon type={iconType} /> : null}
      <strong className={styles.MiniStatusLabelText}>{children}</strong>
      {count !== undefined ? (
        <span
          className={clsx(isRefreshEnabled ? 'sr-only' : 'fgColor-muted', styles.MiniStatusRatio)}
          data-testid="mini-status-count"
        >
          {total === 0 ? count : `${count}/${total}`}
        </span>
      ) : null}
      {status ? <VisuallyHidden>{status}</VisuallyHidden> : null}
    </span>
  )
}

type ProgressBaseProps = {
  'aria-label': string
  successful: number
  total: number
}

type LegacyProgressProps = ProgressBaseProps & {
  isUnavailable: boolean
  unavailableLabel: string
}

function Progress(props: LegacyProgressProps) {
  if (props.isUnavailable) {
    return (
      <div
        className={clsx(styles.MiniStatusProgress, styles.MiniStatusUnavailableProgress)}
        data-testid="mini-status-unavailable-progress"
      >
        <span className="sr-only">{props.unavailableLabel}</span>
      </div>
    )
  }

  const isEmpty = props.total === 0
  return (
    <ProgressBar
      aria-label={props['aria-label']}
      aria-valuenow={isEmpty ? 0 : undefined}
      bg={isEmpty ? 'neutral.emphasis' : undefined}
      className={styles.MiniStatusProgress}
      progress={isEmpty ? 100 : (props.successful / props.total) * 100}
    />
  )
}

MiniMergeStatusItem.LabelRow = LabelRow
MiniMergeStatusItem.Label = Label
MiniMergeStatusItem.Progress = Progress

export {MiniMergeStatusItem}
